/**
 * Portfolio Data Service
 * Loads portfolio data from local file or CDN
 * 
 * To use a CDN:
 * 1. Upload portfolioData.json to your CDN
 * 2. Update CDN_BASE_URL below
 * 3. Set USE_CDN to true
 * 4. Use fetchPortfolioData() instead of getPortfolioData()
 */

// Import local data directly (bundled with the app)
import localPortfolioData from '../data/portfolioData.json';

const USE_CDN = false; // Set to true to use CDN
const CDN_BASE_URL = 'https://your-cdn-url.com/data'; // Your CDN base URL

let cachedData = null;

/**
 * Get portfolio data (synchronous for local data)
 * @returns {Object} Portfolio data object
 */
export const getPortfolioData = () => {
  // Return cached data if available
  if (cachedData) {
    return cachedData;
  }

  // Return local data directly (no async needed)
  cachedData = localPortfolioData;
  return cachedData;
};

/**
 * Fetch portfolio data from CDN (async, only used when USE_CDN is true)
 * @returns {Promise<Object>} Portfolio data object
 */
export const fetchPortfolioData = async () => {
  // If using CDN, fetch from CDN
  if (USE_CDN) {
    try {
      const dataUrl = `${CDN_BASE_URL}/portfolioData.json`;
      const response = await fetch(dataUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch portfolio data: ${response.statusText}`);
      }

      const data = await response.json();
      cachedData = data;
      return data;
    } catch (error) {
      console.error('Error fetching portfolio data from CDN:', error);
      // Fallback to local data
      return getPortfolioData();
    }
  }
  
  // Otherwise, return local data synchronously
  return getPortfolioData();
};

/**
 * Get full CDN URL for an asset
 * @param {string} path - Relative path to the asset
 * @param {string} type - Type of asset: 'image', 'asset', or 'data'
 * @returns {string} Full CDN URL
 */
export const getCdnUrl = (path, type = 'image') => {
  if (!USE_CDN || !path) return path;
  
  const cdnPaths = {
    image: '/images',
    asset: '/assets',
    data: '/data'
  };
  
  const basePath = cdnPaths[type] || cdnPaths.image;
  
  // Remove leading slash if present to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  return `${CDN_BASE_URL.replace('/data', '')}${basePath}/${cleanPath}`;
};

/**
 * Clear cached data (useful for development/testing)
 */
export const clearCache = () => {
  cachedData = null;
};

